import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  AddOnItem,
  AddOnItemName,
  BentoCategory,
  Organization,
  OrganizationNiche,
  OrganizationProfile,
  OrganizationVaProfile,
  SearchParams,
  TemplateIntakeForm,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import Alert from "components/Alert";
import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useAlert } from "utils/useAlert";

import AboutForm from "../Onboarding/AboutForm";
import { getUserType } from "../Onboarding/AboutForm/helpers";
import SocialMediaLinksForm from "../Onboarding/SocialMediaLinksForm";
import SelfServeVaComplete from "./Complete";
import NichePick from "./NichePick";
import CopywritersFlow from "./TemplatesPick/CopywritersFlow";
import CreateYourOwn from "./TemplatesPick/CreateYourOwnFlow";
import Welcome from "./Welcome";
import {
  getQuantityForItem,
  redirectToStep,
  vaSelfServeNavigate,
} from "./helpers";
import styles from "./styles";

const SelfServeVaOnboarding = () => {
  const {
    profile,
    emailSettings,
    currentOrg,
    setCurrentUser,
    setCurrentOrg,
    setUserNotifications,
    setUserIntegrations,
    setEmailSettings,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
    organizationVaProfile,
    setOrganizationVaProfile,
    setProfile,
  } = useContext(OrganizationUserContext);
  const theme = useTheme();
  const slideContext = useContext(SlideAnimationContext);
  const { setSubscription, subscription } = useContext(SubscriptionContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const { slideInUp, slideProps } = useContext(SlideAnimationContext);
  const [message, severity, setAlert, closeAlert] = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const source = getSource();
  const [bundledAddOns, setBundledAddOns] = useState<AddOnItem[]>([]);

  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);
  const numOfNiche =
    getQuantityForItem(AddOnItemName.niche, bundledAddOns) || 0;
  const numOfTemplates =
    getQuantityForItem(AddOnItemName.pitching_templates, bundledAddOns) || 0;
  const numOfCall =
    getQuantityForItem(AddOnItemName.support_call, bundledAddOns) || 0;

  const templateAddOn = bundledAddOns.find(
    (item) => item.itemName === AddOnItemName.pitching_templates,
  );
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);
  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [templateIntakeForms, setTemplateIntakeForms] = useState<
    TemplateIntakeForm[]
  >([]);

  const backgroundDetails =
    organizationVaProfile?.backgroundDetails ||
    profile?.backgroundDetails ||
    {};
  const skipGeneratingBackgroundDetails =
    backgroundDetails &&
    Object.keys(backgroundDetails).length > 0 &&
    profile?.onboardingGoogleDocId &&
    profile?.onboardingGoogleDocId?.length > 0;

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      const { niches } = await fetcherAuth(
        getToken,
        `/api/organization/${res.organization.id}/organization-niches`,
      );
      setFetchUserLoading(false);
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setOnboardingForm((prev) => ({
        ...prev,
        ...res.organizationProfile,
        name: res.organizationUser.name,
        pitchEmail: res.organizationProfile?.pitchEmail,
        userType: getUserType(res.organizationProfile),
      }));
      setIncludedBentoSearchFilterIds(res.includedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(res.excludedBentoSearchFilterIds);
      setOrganizationVaProfile({
        ...res.organizationVaProfile,
        niches,
      });
      setProfile(res.organizationProfile);
      redirectToRightPage(
        res.organization,
        res.organizationVaProfile,
        res.organizationProfile,
      );
      slideContext.slideInUp();
    } catch (error) {}
  };

  const getCategoriesToPickFrom = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    }
  };

  const saveOrgVaProfile = async (
    orgVaProfile: OrganizationVaProfile | null,
    excludeIncludeFilterHasChanged?: boolean,
    moveNext = true,
    updateNiche = false,
  ) => {
    setLoading(true);
    if (excludeIncludeFilterHasChanged) {
      handleSaveFilters();
    }
    if (updateNiche && orgVaProfile?.niches) {
      const noBentoCategoryIds = orgVaProfile.niches.filter(
        (niche) => !niche.bentoCategoryId,
      );
      if (noBentoCategoryIds.length > 0) {
        setAlert("Please select a category for each niche", "error");
        setLoading(false);
        return;
      }

      saveNiches(orgVaProfile.niches);
    }
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/va-profile`,
        "PUT",
        {},
        {
          ...orgVaProfile,
          page: location.pathname,
          skipGeneratingBackgroundDetails: Boolean(
            skipGeneratingBackgroundDetails,
          ),
          source,
        },
      );
      setOrganizationVaProfile(res.organizationVaProfile);

      if (moveNext) {
        if (slideContext) {
          await slideContext.slideOutUp();
          slideContext.slideInUp();
        }
        navigate(
          `/${vaSelfServeNavigate(location.pathname, "next")}${location.search}`,
        );
      }
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const saveOrgProfile = async (onboardingForm: Map, moveNext = true) => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...onboardingForm,
          page: location.pathname,
          source,
          isVaFlow: true,
        },
      );
      if (moveNext) {
        if (slideContext) {
          await slideContext.slideOutUp();
          slideContext.slideInUp();
        }
        navigate(
          `/${vaSelfServeNavigate(location.pathname, "next")}${location.search}`,
        );
      }
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const saveNiches = async (niches: OrganizationNiche[]) => {
    const ids = new Set();
    // @ts-ignore: Make sure user pick unique BentoCategoryId
    const uniqueNiches = niches.filter(
      ({ bentoCategoryId }) =>
        !ids.has(bentoCategoryId) && ids.add(bentoCategoryId),
    );
    try {
      setLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/bulk-upsert`,
        "POST",
        {},
        {
          addOnItemId: templateAddOn?.id,
          niches: uniqueNiches,
        },
      );
      setOrganizationVaProfile((prev) => {
        const copy = makeDeepCopy(prev);
        copy.niches = niches;
        return copy;
      });
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAbout = async () => {
    await saveOrgProfile(onboardingForm);

    if (slideContext) {
      await slideContext.slideOutUp();
      slideContext.slideInUp();
    }
    navigate(
      `/${vaSelfServeNavigate(location.pathname, "next")}${location.search}`,
    );
  };

  const handleSaveFilters = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/search-filters`,
        "PUT",
        {},
        {
          include: includedBentoSearchFilterIds,
          exclude: excludedBentoSearchFilterIds,
        },
      );
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    }
  };

  const redirectToRightPage = async (
    organization: Organization,
    organizationVaProfile: OrganizationVaProfile,
    organizationProfile: OrganizationProfile,
  ) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${organization?.id}/payments-add-ons/va-bundled-items`,
      );
      setBundledAddOns(res.addOnItems);
      const hasTemplate =
        organizationVaProfile?.niches?.every(
          (x: OrganizationNiche) => x.emailSettingId || x?.needsTemplate,
        ) &&
        emailSettings?.isDefault &&
        emailSettings?.emailSettingTemplates &&
        emailSettings?.emailSettingTemplates?.length > 0;

      const routeToReturnTo = redirectToStep(
        organization,
        organizationVaProfile,
        organizationProfile,
        res.addOnItems,
        hasTemplate,
        window.location.href,
      );
      navigate(`/${routeToReturnTo}${location.search}`);
      slideInUp();
    } catch (error) {
      setAlert(error?.message || "Please refresh and try again", "error");
    }
  };

  const fetchBundledAddOns = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/va-bundled-items`,
      );
      const templateIntakeForms = res.addOnItems.find(
        (item: AddOnItem) =>
          item?.templateIntakeForms && item?.templateIntakeForms?.length > 0,
      )?.templateIntakeForms;
      if (templateIntakeForms) {
        setTemplateIntakeForms(templateIntakeForms);
      }
      setBundledAddOns(res.addOnItems);
    } catch (error) {
      setAlert(error?.message || "Please refresh and try again", "error");
    }
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch outreach templates to store for default pitch email.
    if (currentOrg?.id) {
      fetchBundledAddOns();
      getCategoriesToPickFrom();
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <>
      <Grid container sx={styles.root}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid sx={styles.formContainer} item xs={12}>
            <Routes>
              <Route
                path="*"
                element={
                  <Navigate
                    to={`/${routes.welcomeSelfServeOnboarding}`}
                    replace
                  />
                }
              />
              <Route path="/welcome" element={<Welcome />} />
              <Route
                path="/about"
                element={
                  <AboutForm
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    handleNext={handleSubmitAbout}
                    hideDiscoveryQuestion={Boolean(referralCode)}
                    isVAFlow
                  />
                }
              />
              <Route
                path="/niches"
                element={
                  <NichePick
                    categoriesToPickFrom={categoriesToPickFrom}
                    numOfNiche={numOfNiche}
                    saveOrgVaProfile={saveOrgVaProfile}
                    organizationVaProfile={organizationVaProfile}
                    templateIntakeForms={templateIntakeForms}
                    setOrganizationVaProfile={setOrganizationVaProfile}
                  />
                }
              />

              {/* Template routes: bring your own */}
              <Route
                path="/templates/method"
                element={
                  <CreateYourOwn
                    saveOrgVaProfile={saveOrgVaProfile}
                    organizationVaProfile={organizationVaProfile}
                    organizationProfile={onboardingForm as OrganizationProfile}
                    numsOfTemplate={numOfTemplates}
                    loading={loading}
                  />
                }
              />
              {/* Template routes: copywriters */}
              <Route
                path="/templates/copywriter"
                element={
                  <>
                    {templateAddOn?.id && (
                      <CopywritersFlow
                        handleNext={async () => {
                          if (slideContext) {
                            await slideContext.slideOutUp();
                            slideContext.slideInUp();
                          }
                          navigate(
                            `/${routes.faqSelfServeOnboarding}${location.search}`,
                          );
                        }}
                        addOnItemId={templateAddOn?.id}
                      />
                    )}
                  </>
                }
              />
              <Route
                path="/socials"
                element={
                  <SocialMediaLinksForm
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    handleNext={async () => {
                      await saveOrgProfile(onboardingForm);
                      if (slideContext) {
                        await slideContext.slideOutUp();
                        slideContext.slideInUp();
                      }
                      navigate(
                        `/${routes.faqSelfServeOnboarding}${location.search}`,
                      );
                    }}
                    loading={loading}
                    handleBack={async () => {
                      if (slideContext) {
                        await slideContext.slideOutDown();
                        slideContext.slideInDown();
                      }
                      navigate(
                        `/${routes.templatesMethodSelfServeOnboarding}${location.search}`,
                      );
                    }}
                    handleSkip={async () => {
                      if (slideContext) {
                        await slideContext.slideOutUp();
                        slideContext.slideInUp();
                      }
                      navigate(
                        `/${routes.faqSelfServeOnboarding}${location.search}`,
                      );
                    }}
                  />
                }
              />
              <Route
                path="/faq"
                element={
                  <SelfServeVaComplete
                    boughtTemplates={numOfTemplates > 0}
                    organizationVaProfile={organizationVaProfile}
                    saveOrgVaProfile={saveOrgVaProfile}
                    bookedCall={Boolean(
                      numOfCall > 0 || subscription?.strategyPackageData,
                    )}
                    loading={loading}
                  />
                }
              />
            </Routes>
          </Grid>
        </Slide>
      </Grid>

      <Alert message={message} severity={severity} closeAlert={closeAlert} />
    </>
  );
};

export default function VAOnboarding() {
  return (
    <SlideAnimationProvider>
      <SelfServeVaOnboarding />
    </SlideAnimationProvider>
  );
}
